import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Button,
} from "@mui/material";
import {
  SmartToy,
  Psychology,
  TrendingUp,
  Insights,
  AutoAwesome,
  School,
  Speed,
  PersonalVideo,
} from "@mui/icons-material";
import Layout from "../components/layout/LandingPageLayout";
import { useTheme } from "@mui/material/styles";
import { Link } from "react-router-dom";

const ChatBotInfo = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <SmartToy sx={{ fontSize: 40 }} />,
      title: "AI-Powered Assistant",
      subtitle: "Smart Conversations",
      description:
        "Chat naturally with an AI trained specifically on accounting principles and your business data.",
    },
    {
      icon: <Psychology sx={{ fontSize: 40 }} />,
      title: "Deep Learning",
      subtitle: "Custom Training",
      description:
        "Our LLM is trained on your specific data to understand your business inside and out.",
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      title: "Predictive Analytics",
      subtitle: "Future Insights",
      description:
        "Get predictions on cash flow, expenses, and revenue trends based on your historical data.",
    },
    {
      icon: <Insights sx={{ fontSize: 40 }} />,
      title: "Business Insights",
      subtitle: "Data-Driven",
      description:
        "Discover hidden patterns and opportunities in your financial data with AI analysis.",
    },
    {
      icon: <AutoAwesome sx={{ fontSize: 40 }} />,
      title: "Instant Answers",
      subtitle: "24/7 Support",
      description:
        "Get immediate answers to accounting questions, anytime, without waiting for support.",
    },
    {
      icon: <School sx={{ fontSize: 40 }} />,
      title: "Accounting Expert",
      subtitle: "Specialized Knowledge",
      description:
        "Trained on comprehensive accounting standards, tax laws, and compliance requirements.",
    },
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: "Quick Processing",
      subtitle: "Real-Time",
      description:
        "Process complex queries and generate reports in seconds, not hours.",
    },
    {
      icon: <PersonalVideo sx={{ fontSize: 40 }} />,
      title: "Personalized Experience",
      subtitle: "For Every User",
      description:
        "Each user gets a customized AI model trained on their unique business patterns.",
    },
  ];

  const capabilities = [
    {
      title: "Ask Anything",
      items: [
        "Financial queries",
        "Tax questions",
        "Compliance help",
        "Report generation",
      ],
    },
    {
      title: "Get Predictions",
      items: [
        "Cash flow forecasts",
        "Expense trends",
        "Revenue projections",
        "Budget planning",
      ],
    },
    {
      title: "Receive Insights",
      items: [
        "Cost optimization",
        "Anomaly detection",
        "Growth opportunities",
        "Risk analysis",
      ],
    },
  ];

  return (
    <Layout>
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.primary.main}03 100%)`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${theme.palette.primary.main}15 0%, transparent 70%)`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -150,
            left: -150,
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${theme.palette.primary.main}10 0%, transparent 70%)`,
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* Header Section */}
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Chip
              label="AI CHATBOT"
              sx={{
                mb: 2,
                bgcolor: theme.palette.primary.main,
                color: "white",
                fontWeight: 600,
                letterSpacing: 1.2,
                px: 2,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Your AI Accounting Assistant
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "text.secondary",
                maxWidth: 700,
                mx: "auto",
                lineHeight: 1.7,
                fontSize: { xs: "1rem", md: "1.1rem" },
              }}
            >
              Powered by advanced LLM technology, trained on your data to
              provide personalized insights, predictions, and expert accounting
              guidance.
            </Typography>
          </Box>

          {/* Chat Demo Mockup */}
          <Box
            sx={{
              mb: 8,
              p: 4,
              borderRadius: 3,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              maxWidth: 700,
              mx: "auto",
              boxShadow: `0 8px 32px ${theme.palette.primary.main}15`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: theme.palette.primary.main,
                  width: 48,
                  height: 48,
                }}
              >
                <SmartToy />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  AI Assistant
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Online • Trained on your data
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* User Message */}
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Box
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: "white",
                    p: 2,
                    borderRadius: 2,
                    maxWidth: "70%",
                    borderBottomRightRadius: 4,
                  }}
                >
                  <Typography variant="body2">
                    What's my cash flow prediction for next month?
                  </Typography>
                </Box>
              </Box>

              {/* Bot Response */}
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Box
                  sx={{
                    bgcolor: "action.hover",
                    p: 2,
                    borderRadius: 2,
                    maxWidth: "70%",
                    borderBottomLeftRadius: 4,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.primary" }}>
                    Based on your historical data, I predict a positive cash
                    flow of ₹2.4L next month, with a confidence of 89%. This is
                    15% higher than last month.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Features Grid */}
          <Grid container spacing={3} sx={{ mb: 8 }}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                    border: "1px solid",
                    borderColor: "divider",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 12px 24px ${theme.palette.primary.main}20`,
                      borderColor: theme.palette.primary.main,
                      "& .feature-icon": {
                        transform: "scale(1.1) rotate(5deg)",
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      height: "100%",
                    }}
                  >
                    <Box
                      className="feature-icon"
                      sx={{
                        color: theme.palette.primary.main,
                        mb: 2,
                        transition: "all 0.3s ease",
                        opacity: 0.9,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        color: "text.primary",
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        mb: 1.5,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      {feature.subtitle}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.6,
                        fontSize: "0.875rem",
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Capabilities Section */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                textAlign: "center",
                mb: 5,
                color: "text.primary",
              }}
            >
              What Can You Do?
            </Typography>
            <Grid container spacing={4}>
              {capabilities.map((capability, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Box
                    sx={{
                      p: 4,
                      borderRadius: 3,
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                      height: "100%",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        transform: "translateY(-4px)",
                        boxShadow: `0 8px 16px ${theme.palette.primary.main}15`,
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                        color: theme.palette.primary.main,
                      }}
                    >
                      {capability.title}
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                      {capability.items.map((item, idx) => (
                        <Typography
                          component="li"
                          variant="body2"
                          key={idx}
                          sx={{
                            color: "text.secondary",
                            mb: 1,
                            lineHeight: 1.6,
                          }}
                        >
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* How It Learns Section */}
          <Box
            sx={{
              p: 5,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.main}05 100%)`,
              border: "1px solid",
              borderColor: theme.palette.primary.main + "30",
              mb: 6,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mb: 3,
                textAlign: "center",
                color: "text.primary",
              }}
            >
              Personalized Learning Process
            </Typography>
            <Grid container spacing={3}>
              {[
                {
                  step: "1",
                  title: "Data Collection",
                  desc: "We securely analyze your accounting data and business patterns",
                },
                {
                  step: "2",
                  title: "Model Training",
                  desc: "Train a dedicated LLM specifically for your business needs",
                },
                {
                  step: "3",
                  title: "Continuous Learning",
                  desc: "The AI adapts and improves as your business grows",
                },
              ].map((item, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Box
                    sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        width: 40,
                        height: 40,
                        fontWeight: 700,
                      }}
                    >
                      {item.step}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, mb: 0.5 }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", lineHeight: 1.6 }}
                      >
                        {item.desc}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Bottom CTA Section */}
          <Box
            sx={{
              mt: 8,
              p: 4,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.1,
                background:
                  "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.05) 10px, rgba(255,255,255,0.05) 20px)",
              }}
            />
            <Typography
              variant="h5"
              sx={{
                color: "white",
                fontWeight: 700,
                mb: 1,
                position: "relative",
                zIndex: 1,
              }}
            >
              Experience the Future of Accounting
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255,255,255,0.9)",
                position: "relative",
                zIndex: 1,
              }}
            >
              Start chatting with your AI assistant today and unlock powerful
              insights
            </Typography>
            <Box>
              <Button
                component={Link}
                to="/login"
                variant="contained"
                size="large"
                sx={{
                  bgcolor: theme.palette.background.paper,
                  color: theme.palette.primary.main,
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  "&:hover": {
                    bgcolor: theme.palette.grey[100],
                  },
                }}
              >
                Get Started Free
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Layout>
  );
};

export default ChatBotInfo;
